import { usePageBannerConfig } from '@/components/base/Layout/PageBanner/hooks/usePageBannerConfig';
import { ActiveFiltersContainer, FiltersOverlay } from '@/components/filter';
import { useAuthContext } from '@/context/AuthContext';
import FiltersContext from '@/context/FiltersContext/FiltersContext';
import { hideOnScrollClasses, useApplyBodyStyle, useHideOnScroll, useUserProfile } from '@/hooks';
import useBannerCollapseStore from '@/hooks/Scroll/useBannerCollapseStore';
import { SESSION_STORAGE_SYNC_EVENT } from '@/hooks/useSessionStorage';
import If from '@/storybook/components/If/If';
import { AppRoutes } from '@/utils';
import collectAllLocalFilters from '@/utils/filter/collectAllLocalFilters';
import { isNonEmptyFilterModule } from '@/utils/filter/isNonEmptyFilterModule';
import { Filter } from '@cubejs-client/core';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import ApplicationHeader from './components/ApplicationHeader/ApplicationHeader';
import NavigationBar from './components/NavigationBar/NavigationBar';
import { useIsFilterComponentVisible } from './components/NavigationBar/routeExclusions';
import styles from './Header.module.css';

export const HEIGHT_APP_HEADER = 68;
export const HEIGHT_NAVBAR = 50;
export const HEIGHT_FILTERS = 48;

interface HeaderOffsetOptions {
  withNavbar?: boolean;
  withFilters?: boolean;
  isFilterComponentVisible?: boolean;
}

export const getHeaderOffset = ({
  withNavbar = false,
  withFilters = false,
  isFilterComponentVisible = false,
}: HeaderOffsetOptions): number => {
  const navbarOffset = withNavbar ? HEIGHT_NAVBAR : 0;
  const filtersOffset = withNavbar && withFilters && isFilterComponentVisible ? HEIGHT_FILTERS : 0;

  return HEIGHT_APP_HEADER + navbarOffset + filtersOffset;
};

interface HeaderProps {
  tierFilter?: Filter;
  ppocFilter?: Filter;
}

const Header = ({ tierFilter, ppocFilter }: HeaderProps) => {
  const location = useLocation();
  const isManagePreferencesPath = Boolean(matchPath(`${AppRoutes.MANAGE_PREFERENCES}/*`, location.pathname));

  const user = useUserProfile();
  const { isLoggedIn } = useAuthContext();
  const { activeFilters } = useContext(FiltersContext);
  const headerContainerRef = useRef<HTMLDivElement>(null);
  const { syncCollapse } = usePageBannerConfig();
  const isBannerCollapsed = useBannerCollapseStore(state => state.isCollapsed);

  const hasLocalFilters = useCallback(() => {
    const local = collectAllLocalFilters();
    return Object.values(local).some(isNonEmptyFilterModule);
  }, []);

  const [localFiltersActive, setLocalFiltersActive] = useState(hasLocalFilters);

  useEffect(() => {
    const handleSync = () => setLocalFiltersActive(hasLocalFilters());
    window.addEventListener(SESSION_STORAGE_SYNC_EVENT, handleSync);
    return () => window.removeEventListener(SESSION_STORAGE_SYNC_EVENT, handleSync);
  }, [hasLocalFilters]);

  const hasActiveFilters = activeFilters.length > 0 || localFiltersActive;

  const showNavbar = Boolean(user?.registrationDone && isLoggedIn && !isManagePreferencesPath);

  const isFilterComponentVisible = useIsFilterComponentVisible(location.pathname);

  const bodyStyle = useMemo(
    () => ({
      paddingTop:
        getHeaderOffset({
          withNavbar: showNavbar,
          withFilters: hasActiveFilters,
          isFilterComponentVisible,
        }) + 'px',
    }),
    [showNavbar, hasActiveFilters, isFilterComponentVisible],
  );

  useApplyBodyStyle(bodyStyle);

  useHideOnScroll({
    targetRef: headerContainerRef,
    hideOnScrollClass: hideOnScrollClasses.offset120,
    overrideHidden: syncCollapse ? isBannerCollapsed : undefined,
  });

  return (
    <div className={styles.header} ref={headerContainerRef}>
      <header className={styles.content}>
        <ApplicationHeader />
        <If condition={showNavbar}>
          <NavigationBar tierFilter={tierFilter} ppocFilter={ppocFilter} />
          <If condition={hasActiveFilters && isFilterComponentVisible}>
            <ActiveFiltersContainer />
          </If>
        </If>
      </header>
      <FiltersOverlay />
    </div>
  );
};

export default Header;
